<script lang="ts">
import { useTailwind } from '@/plugins/useTailwind'
import { useInstantWinsStore } from '@/stores/instant-wins'
import { usePrizesStore } from '@/stores/prizes'
import type { InstantWinDraw, Prize } from '@/types'
import type { TIconValue } from '@/utils/helper-objects'
import { filterGames } from '@/utils/utils'
import { ThemeType } from '@goodlifeplus/themes'
import { defineComponent } from 'vue'

import { RealTimeEvents } from '../../services/real-time-events'
import PrizeCardWide from '../cards/PrizeCardWide.vue'
import ComingSoonCard from '../cards/new/ComingSoonCard.vue'
import GameCardSkeleton from '../cards/new/GameCardSkeleton.vue'
import InstantWinGameCard from '../cards/new/InstantWinGameCard.vue'
import AppCarousel from '../carousel/AppCarousel.vue'
import CarouselItem from '../carousel/CarouselItem.vue'
import GameCarousel from '../carousel/GameCarousel.vue'
import MobileGameCarousel from '../carousel/MobileGameCarousel.vue'

export default defineComponent({
  components: {
    AppCarousel,
    CarouselItem,
    ComingSoonCard,
    GameCarousel,
    GameCardSkeleton,
    InstantWinGameCard,
    MobileGameCarousel,
    PrizeCardWide,
  },
  setup() {
    const prizesStore = usePrizesStore()
    const instantWinsStore = useInstantWinsStore()
    const { breakpoints } = useTailwind()

    return {
      breakpoints,
      instantWinsStore,
      prizesStore,
    }
  },
  data() {
    return {
      featuredPrizes: [] as Prize[],
      columnsPerRow: 4,
      resizeObserver: null as ResizeObserver | null,
      remainingColumns: 0,
    }
  },
  computed: {
    featuredPrizesIcon(): TIconValue {
      return this.$theme.type === ThemeType.Club
        ? this.Icons.Football
        : this.Icons.Entries
    },
    instantWinGames(): Array<InstantWinDraw> {
      return filterGames(
        this.instantWinsStore.futureInstantWinDraws.filter(
          (prize) => prize.is_visible ?? true
        ),
        {
          minGames: 0,
          featuredOnly: true,
        }
      )
    },
    isLoadingInstantWins(): boolean {
      return this.instantWinsStore.isLoading
    },
    isLoadingPrizes(): boolean {
      return this.prizesStore.isLoading
    },
    hasInstantWinsError(): boolean {
      return (
        this.instantWinsStore.hasError &&
        this.instantWinsStore.instantWins.size === 0
      )
    },
    hasPrizesError(): boolean {
      return this.prizesStore.hasError && this.prizesStore.prizes.size === 0
    },
    featuredPrizesColumns(): Prize[][] {
      const columns: Prize[][] = []
      const cols = this.columnsPerRow
      const pageSize = cols * 2
      // Group prizes: top row then bottom row, based on visible columns
      for (let page = 0; page < this.featuredPrizes.length; page += pageSize) {
        const pageItems = this.featuredPrizes.slice(page, page + pageSize)
        const topRow = pageItems.slice(0, cols)
        const bottomRow = pageItems.slice(cols, pageSize)
        for (let i = 0; i < topRow.length; i++) {
          const col = [topRow[i]]
          if (bottomRow[i]) {
            col.push(bottomRow[i])
          }
          columns.push(col)
        }
      }
      return columns
    },
    isInstantWinsMobile(): boolean {
      return this.columnsPerRow <= 2
    },
  },
  watch: {
    // Watch for prize store changes to update featured prizes
    'prizesStore.prizesArray.length': {
      handler(newLength: number) {
        if (newLength > 0) {
          this.updatePrizes()
        }
      },
      immediate: true,
    },
    instantWinGames: {
      handler(newItems: Array<InstantWinDraw>) {
        if (this.isInstantWinsMobile && newItems.length > 0) {
          const lastPageLength = newItems.length % 4
          this.remainingColumns = lastPageLength > 0 ? 4 - lastPageLength : 0
        } else {
          this.remainingColumns = this.columnsPerRow - newItems.length
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.setupResizeObserver()
  },
  beforeUnmount() {
    this.resizeObserver?.disconnect()
    RealTimeEvents.unsubscribe(
      RealTimeEvents.Channels.InstantWinResults,
      RealTimeEvents.Events.InstantWinResultsUpdate
    )
  },
  methods: {
    setupResizeObserver() {
      const mainContainer = document.querySelector('main')
      if (!mainContainer) {
        return
      }

      this.resizeObserver = new ResizeObserver(([entry]) => {
        const width = entry.contentRect.width
        if (width >= this.breakpoints.lg) {
          this.columnsPerRow = 4
        } else if (width >= this.breakpoints.md) {
          this.columnsPerRow = 3
        } else {
          this.columnsPerRow = 2
        }

        if (this.isInstantWinsMobile && this.instantWinGames.length > 0) {
          const lastPageLength = this.instantWinGames.length % 4
          this.remainingColumns = lastPageLength > 0 ? 4 - lastPageLength : 0
        } else {
          this.remainingColumns =
            this.columnsPerRow - this.instantWinGames.length
        }
      })
      this.resizeObserver.observe(mainContainer)
    },
    updatePrizes() {
      const sortedPrizes = this.prizesStore.futurePrizes.sort(
        (a: Prize, b: Prize) => {
          const aTime = a.close_at?.toMillis() || a.drawn_at.toMillis()
          const bTime = b.close_at?.toMillis() || b.drawn_at.toMillis()
          return aTime - bTime
        }
      )

      // Get the closest prize by date
      const closestPrize = sortedPrizes[0]

      // Remaining prizes (exclude closest if present)
      const remaining = closestPrize
        ? sortedPrizes.filter((prize) => prize !== closestPrize)
        : sortedPrizes

      // Split remaining prizes by value
      const mainPrizes = remaining.filter(
        (prize) => prize.value >= this.BIG_PRIZE_VALUE
      )
      const regularPrizes = remaining.filter(
        (prize) => prize.value < this.BIG_PRIZE_VALUE
      )

      // Order: closest, main, regular
      const orderedPrizes = [
        ...(closestPrize ? [closestPrize] : []),
        ...mainPrizes,
        ...regularPrizes,
      ]

      this.featuredPrizes = orderedPrizes.filter(
        (prize: Prize) => prize.is_visible ?? true
      )
    },
  },
})
</script>

<template>
  <!-- Instant Prizes -->
  <AppSection v-if="isLoadingInstantWins">
    <div class="mb-4 flex items-center gap-3 sm:gap-4">
      <AppIcon :icon="Icons.TicketStar" size="xs" class="sm:h-8 sm:w-8" />
      <h3 class="text-lg font-bold sm:text-2xl">Featured Instant Prizes</h3>
    </div>
    <GameCardSkeleton :count="4" />
  </AppSection>
  <AppSection v-else-if="hasInstantWinsError">
    <div class="py-12 text-center text-slate-600">
      <p class="text-lg">Unable to load games right now.</p>
      <p class="mt-2">Please check back later.</p>
    </div>
  </AppSection>
  <AppSection v-if="instantWinGames.length">
    <!-- Mobile Grid Carousel (2x2 pages, horizontal scroll) -->
    <div v-if="isInstantWinsMobile">
      <MobileGameCarousel
        :games="instantWinGames"
        title="Featured Instant Prizes"
        :icon="Icons.TicketStar"
        :remaining-columns="remainingColumns"
      >
        <template #default="{ page }">
          <InstantWinGameCard
            v-for="prize in page"
            :key="prize.code"
            :game="prize"
          />
        </template>
        <template #empty="{ emptySlots }">
          <ComingSoonCard v-if="emptySlots > 0" :empty-slots="emptySlots" />
        </template>
      </MobileGameCarousel>
    </div>
    <!-- Desktop Carousel (uses GameCarousel component) -->
    <GameCarousel
      v-else
      :key="`instant-${instantWinGames.length}`"
      :prizes="instantWinGames"
      title="Featured Instant Prizes"
      :icon="Icons.TicketStar"
      :view-all-route="{ name: 'prizes', query: { switch: 'instant-wins' } }"
      :rows="2"
      :columns-per-row="columnsPerRow"
      :remaining-columns="remainingColumns"
    >
      <template #default="{ prize }">
        <InstantWinGameCard :key="prize.code" :game="prize" />
      </template>
      <template #empty="{ emptySlots }">
        <ComingSoonCard v-if="emptySlots > 0" :empty-slots="emptySlots" />
      </template>
    </GameCarousel>
  </AppSection>

  <!-- 
    - Members Prizes 
  -->
  <AppSection v-if="isLoadingPrizes">
    <div class="mb-4 flex items-center gap-3 sm:gap-4">
      <AppIcon
        :icon="$t('section.featured_prizes.heading_icon')"
        size="xs"
        class="sm:h-8 sm:w-8"
      />
      <h3 class="text-lg font-bold sm:text-2xl">
        {{ $t('section.featured_prizes.heading') }}
      </h3>
    </div>
    <GameCardSkeleton :count="4" />
  </AppSection>
  <AppSection v-else-if="hasPrizesError">
    <div class="py-12 text-center text-slate-600">
      <p class="text-lg">Unable to load prizes right now.</p>
      <p class="mt-2">Please check back later.</p>
    </div>
  </AppSection>
  <AppSection v-else-if="featuredPrizes.length">
    <AppCarousel
      :title="$t('section.featured_prizes.heading')"
      :icon="$t('section.featured_prizes.heading_icon')"
      :view-all-route="{
        name: 'prizes',
        query: { switch: 'featured-prizes' },
      }"
    >
      <CarouselItem
        v-for="(column, index) in featuredPrizesColumns"
        :key="index"
        class="flex w-1/2 flex-col gap-3 @md/main:w-1/3 @lg/main:w-1/4"
      >
        <PrizeCardWide v-for="prize in column" :key="prize.code" :prize />
      </CarouselItem>
    </AppCarousel>
  </AppSection>
</template>
